/**
 * Ranking das empresas no periodo: top 5 por recuperado, por comissao ou pela
 * maior alta contra o periodo anterior.
 *
 * Decisoes nao obvias:
 *  - A barra mostra a PARTICIPACAO no total (bps inteiros de `participacaoBps`)
 *    e o percentual vem escrito ao lado: a largura sozinha nao e leitura.
 *  - "Maior alta" so considera quem tem base anterior. Empresa nova com "+∞%"
 *    ganharia sempre e nao ensinaria nada.
 *  - O nome e um link para o detalhe (preservando o periodo da URL); "ver
 *    todas" ordena a lista abaixo pela mesma medida.
 */

import { Link } from 'react-router-dom';
import { Button, CompanyLogo, FilterChips, SectionCard, type ChipOption } from '../../../components/ui';
import { formatBRLFromCents, formatPercentFromBps } from '../../../lib/format';
import { participacaoBps, variar } from '../comparar';
import type { LinhaEmpresa } from '../lista';
import type { ColunaLista } from '../types';
import { Delta } from './Delta';

export type MedidaRanking = 'recuperado' | 'comissao' | 'alta';

const OPCOES: readonly ChipOption<MedidaRanking>[] = [
  { value: 'recuperado', label: 'Recuperado' },
  { value: 'comissao', label: 'Comissão' },
  { value: 'alta', label: 'Maior alta' },
];

const COLUNA: Readonly<Record<MedidaRanking, ColunaLista>> = { recuperado: 'recuperado', comissao: 'comissao', alta: 'variacao' };

interface Posicao {
  readonly linha: LinhaEmpresa;
  readonly valor: number;
  readonly anterior: number | null;
}

function montar(linhas: readonly LinhaEmpresa[], medida: MedidaRanking): readonly Posicao[] {
  const comDado = linhas.filter((l) => l.item !== null);
  if (medida === 'alta') {
    return comDado
      .map((l) => {
        const atual = l.item?.metrics.recoveredAmountCents ?? 0;
        const anterior = l.item?.previous?.recoveredAmountCents ?? null;
        return { linha: l, valor: atual, anterior, rel: variar(atual, anterior).relativaBps };
      })
      .filter((p) => p.rel !== null && p.rel > 0)
      .sort((a, b) => (b.rel ?? 0) - (a.rel ?? 0))
      .slice(0, 5);
  }
  return comDado
    .map((l) => {
      const m = l.item?.metrics;
      const p = l.item?.previous ?? null;
      const valor = medida === 'recuperado' ? m?.recoveredAmountCents ?? 0 : m?.commissionCents ?? 0;
      const anterior = p === null ? null : medida === 'recuperado' ? p.recoveredAmountCents : p.commissionCents;
      return { linha: l, valor, anterior };
    })
    .filter((p) => p.valor > 0)
    .sort((a, b) => b.valor - a.valor)
    .slice(0, 5);
}

export function RankingEmpresas({
  linhas,
  medida,
  onMedida,
  linkDe,
  onVerTodas,
  className = '',
}: {
  readonly linhas: readonly LinhaEmpresa[];
  readonly medida: MedidaRanking;
  readonly onMedida: (m: MedidaRanking) => void;
  readonly linkDe: (companyId: string) => string;
  readonly onVerTodas: (coluna: ColunaLista) => void;
  readonly className?: string;
}): JSX.Element {
  const posicoes = montar(linhas, medida);
  const total = posicoes.reduce((acc, p) => acc + p.valor, 0);
  const totalGeral = linhas.reduce((acc, l) => acc + (medida === 'comissao' ? l.item?.metrics.commissionCents ?? 0 : l.item?.metrics.recoveredAmountCents ?? 0), 0);

  return (
    <SectionCard id="ranking" title="Ranking do período" icon="estrela" description="Quem mais trouxe venda de volta." className={className}>
      <FilterChips label="Medida do ranking" options={OPCOES} value={medida} onChange={onMedida} />
      {posicoes.length === 0 ? (
        <p className="mt-4 text-sm text-ink-muted">
          {medida === 'alta' ? 'Nenhuma empresa com base anterior cresceu no período. Escolha uma janela com período anterior (7, 30 ou 90 dias).' : 'Nenhuma venda recuperada no período.'}
        </p>
      ) : (
        <ol className="mt-4 flex flex-col gap-3">
          {posicoes.map((p, i) => {
            const share = participacaoBps(p.valor, medida === 'alta' ? total : totalGeral);
            return (
              <li key={p.linha.company.id} className="flex items-center gap-3">
                <span aria-hidden="true" className={`grid h-6 w-6 shrink-0 place-items-center rounded-full text-[11px] font-semibold ${i === 0 ? 'bg-brand text-white' : 'bg-ink/5 text-ink-muted'}`}>
                  {i + 1}
                </span>
                <CompanyLogo company={p.linha.company} size="sm" />
                <div className="min-w-0 flex-1">
                  <div className="flex items-baseline justify-between gap-2">
                    <Link to={linkDe(p.linha.company.id)} className="truncate text-sm font-medium text-ink hover:text-brand">
                      <span className="sr-only">{i + 1}º: </span>
                      {p.linha.company.name}
                    </Link>
                    <span className="shrink-0 text-sm font-semibold tabular-nums text-ink">{formatBRLFromCents(p.valor)}</span>
                  </div>
                  <div className="mt-1 flex items-center gap-2">
                    <div className="h-1.5 flex-1 overflow-hidden rounded-full bg-ink/10" aria-hidden="true">
                      <div className={`h-full rounded-full ${i === 0 ? 'bg-brand' : 'bg-accent'}`} style={{ width: `${share === null ? 0 : Math.max(2, Math.round(share / 100))}%` }} />
                    </div>
                    <span className="w-12 shrink-0 text-right text-[11px] tabular-nums text-ink-subtle" title={medida === 'alta' ? 'Participação entre as empresas em alta' : 'Participação no total do período'}>
                      {share === null ? '' : formatPercentFromBps(share)}
                    </span>
                    <Delta atual={p.valor} anterior={p.anterior} tipo="dinheiro" compacto />
                  </div>
                </div>
              </li>
            );
          })}
        </ol>
      )}
      <div className="mt-4">
        <Button variant="ghost" size="sm" iconRight="seta-dir" onClick={() => onVerTodas(COLUNA[medida])}>
          Ordenar a lista por {medida === 'alta' ? 'variação' : medida === 'comissao' ? 'comissão' : 'recuperado'}
        </Button>
      </div>
    </SectionCard>
  );
}
